import {
  Component,
  input,
  output,
  ChangeDetectionStrategy,
  ContentChild,
  TemplateRef,
  HostBinding,
  ElementRef,
  Renderer2,
  AfterViewInit,
  OnDestroy,
  effect,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SoPremiumIconUI } from '../public-api';

@Component({
  selector: 'so-modal-layout-ui',
  standalone: true,
  imports: [CommonModule, SoPremiumIconUI],
  templateUrl: './so-modal-layout.component.html',
  styleUrl: './so-modal-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoModalLayoutUI implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private dialogSurface: HTMLElement | null = null;
  private readonly originalDialogSurfaceStyles: Record<'height' | 'max-height' | 'overflow', string | null> = {
    height: null,
    'max-height': null,
    overflow: null,
  };
  private hasCapturedDialogSurfaceStyles = false;

  @ContentChild('headerLeft') headerLeft?: TemplateRef<unknown>;
  @ContentChild('headerRight') headerRight?: TemplateRef<unknown>;
  @ContentChild('footerLeft') footerLeft?: TemplateRef<unknown>;
  @ContentChild('footerRight') footerRight?: TemplateRef<unknown>;
  @ContentChild('footerCenter') footerCenter?: TemplateRef<unknown>;

  // Header configuration
  title = input<string>('');
  subtitle = input<string>('');
  icon = input<string>('');
  iconColor = input<string>('--so-primary');
  iconSize = input<number>(28);
  showHeader = input<boolean>(true);

  // Footer configuration
  showFooter = input<boolean>(true);
  footerAlign = input<'left' | 'center' | 'right' | 'space-between'>('right');

  // Content configuration
  contentDisabled = input<boolean>(false);
  contentPadding = input<string>('1.5rem 2rem');
  surfaceHeightMode = input<'inherit' | 'auto'>('inherit');
  surfaceMaxHeight = input<string>('90vh');
  layoutMinHeight = input<string | null>(null);
  layoutMaxHeight = input<string>('85vh');

  // Outputs
  closed = output<void>();

  @HostBinding('class.so-modal-layout-host--auto')
  get isAutoSurfaceMode(): boolean {
    return this.surfaceHeightMode() === 'auto';
  }

  constructor() {
    effect(() => {
      if (!this.dialogSurface) return;
      this.applyDialogSurfaceStyles();
    });
  }

  ngAfterViewInit(): void {
    this.dialogSurface = this.elementRef.nativeElement.closest('.mat-mdc-dialog-surface, .mdc-dialog__surface');
    if (!this.dialogSurface) return;
    this.captureDialogSurfaceStyles();
    this.applyDialogSurfaceStyles();
  }

  ngOnDestroy(): void {
    this.restoreDialogSurfaceStyles();
  }

  onClose(): void {
    this.closed.emit();
  }

  private captureDialogSurfaceStyles(): void {
    if (!this.dialogSurface || this.hasCapturedDialogSurfaceStyles) return;
    this.originalDialogSurfaceStyles.height = this.dialogSurface.style.height || null;
    this.originalDialogSurfaceStyles['max-height'] = this.dialogSurface.style.maxHeight || null;
    this.originalDialogSurfaceStyles.overflow = this.dialogSurface.style.overflow || null;
    this.hasCapturedDialogSurfaceStyles = true;
  }

  private applyDialogSurfaceStyles(): void {
    if (!this.dialogSurface) return;

    if (this.surfaceHeightMode() !== 'auto') {
      this.restoreDialogSurfaceStyles();
      return;
    }

    this.renderer.setStyle(this.dialogSurface, 'height', 'auto');
    this.renderer.setStyle(this.dialogSurface, 'max-height', this.surfaceMaxHeight());
    this.renderer.setStyle(this.dialogSurface, 'overflow', 'hidden');
  }

  private restoreDialogSurfaceStyles(): void {
    if (!this.dialogSurface || !this.hasCapturedDialogSurfaceStyles) return;
    this.restoreDialogSurfaceStyle('height');
    this.restoreDialogSurfaceStyle('max-height');
    this.restoreDialogSurfaceStyle('overflow');
  }

  private restoreDialogSurfaceStyle(styleName: 'height' | 'max-height' | 'overflow'): void {
    const value = this.originalDialogSurfaceStyles[styleName];
    if (value) {
      this.renderer.setStyle(this.dialogSurface, styleName, value);
      return;
    }
    this.renderer.removeStyle(this.dialogSurface, styleName);
  }
}
